import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  HiOutlineUserPlus, HiOutlineTrash, HiOutlinePauseCircle, HiOutlinePlayCircle,
  HiOutlineArrowLeftCircle, HiOutlineBriefcase, HiOutlinePencilSquare,
  HiOutlineExclamationTriangle
} from 'react-icons/hi2';
import api from '../utils/api';
import TopBar from '../components/TopBar';
import { useRestaurant } from '../context/RestaurantContext';

const initials = (n) => (n || '').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
const currencySymbol = (c) => c === 'EUR' ? '€' : '£';

const ConfirmDialog = ({ open, variant = 'danger', title, message, confirmLabel, cancelLabel = 'Cancel', onConfirm, onCancel, loading }) => {
  if (!open) return null;
  const isDanger = variant === 'danger';
  const accent = isDanger ? 'var(--bad)' : 'var(--warn)';
  const accentBg = isDanger ? 'var(--bad-bg)' : 'var(--warn-bg)';

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420, padding: 28, textAlign: 'center' }}>
        <div style={{
          width: 60, height: 60, borderRadius: '50%',
          background: accentBg, color: accent,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 16px'
        }}>
          <HiOutlineExclamationTriangle size={30} />
        </div>
        <h2 className="modal-title" style={{ marginBottom: 8 }}>{title}</h2>
        <p className="modal-text" style={{ marginBottom: 22 }}>{message}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={loading} style={{ minWidth: 100 }}>
            {cancelLabel}
          </button>
          <button type="button"
            className={isDanger ? 'btn btn-danger' : 'btn btn-warn'}
            onClick={onConfirm} disabled={loading} style={{ minWidth: 120 }}>
            {loading ? '…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const FilterPill = ({ active, onClick, children }) => (
  <button onClick={onClick}
    style={{
      padding: '8px 14px',
      background: active ? 'var(--ink-900)' : 'var(--surface)',
      color: active ? '#fff' : 'var(--text)',
      border: `1.5px solid ${active ? 'var(--ink-900)' : 'var(--border-strong)'}`,
      borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
      fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center',
      transition: 'all 0.15s'
    }}>{children}</button>
);

const UserManagement = () => {
  const nav = useNavigate();
  const { activeRestaurant } = useRestaurant();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const [confirm, setConfirm] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = activeRestaurant ? { restaurantId: activeRestaurant._id } : {};
      const r = await api.get('/users', { params });
      setUsers(r.data.users || []);
    } catch {
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [activeRestaurant?._id]); // eslint-disable-line

  const askToggle = (user) => setConfirm({ type: 'toggle', user });
  const askDelete = (user) => setConfirm({ type: 'delete', user });

  const onConfirm = async () => {
    if (!confirm) return;
    setConfirmLoading(true);
    try {
      if (confirm.type === 'toggle') {
        const r = await api.put(`/users/${confirm.user._id}/toggle`);
        if (r.data.success) toast.success(r.data.message);
      } else if (confirm.type === 'delete') {
        await api.delete(`/users/${confirm.user._id}`);
        toast.success(`${confirm.user.name} deleted`);
      }
      load();
      setConfirm(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setConfirmLoading(false);
    }
  };

  const filtered = filter === 'all'
    ? users
    : users.filter(u => (u.role === filter || (filter === 'employee' && u.role === 'user')));

  const managerCount = users.filter(u => u.role === 'manager').length;
  const employeeCount = users.filter(u => u.role === 'employee' || u.role === 'user').length;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <TopBar title="Users" />
      <div className="page">
        <div className="page-container">
          <div className="page-header">
            <div style={{ flex: 1, minWidth: 0 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => nav('/dashboard')}>
                <HiOutlineArrowLeftCircle size={16} /> Back
              </button>
              <h1 className="page-title" style={{ marginTop: 8 }}>User Management</h1>
              <p className="page-subtitle hide-on-mobile">
                {activeRestaurant ? `Managing users for ${activeRestaurant.name}` : 'Manage user accounts'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-outline-blue" onClick={() => nav('/users/new?role=manager')}>
                <HiOutlineBriefcase size={16} /> Add Manager
              </button>
              <button className="btn btn-accent" onClick={() => nav('/users/new?role=employee')}>
                <HiOutlineUserPlus size={16} /> Add Employee
              </button>
            </div>
            <style>{`
              @media (max-width: 768px) {
                .page-header {
                  flex-direction: column !important;
                  align-items: stretch !important;
                  gap: 14px !important;
                }
                .page-header .page-title {
                  font-size: 22px !important;
                  margin-top: 6px !important;
                }
                .page-header .btn {
                  flex: 1 !important;
                  justify-content: center !important;
                  font-size: 13px !important;
                  padding: 10px 12px !important;
                }
              }
            `}</style>
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
            <FilterPill active={filter === 'all'} onClick={() => setFilter('all')}>
              All <span className="role-chip role-employee" style={{ marginLeft: 6 }}>{users.length}</span>
            </FilterPill>
            <FilterPill active={filter === 'manager'} onClick={() => setFilter('manager')}>
              Managers <span className="role-chip role-manager" style={{ marginLeft: 6 }}>{managerCount}</span>
            </FilterPill>
            <FilterPill active={filter === 'employee'} onClick={() => setFilter('employee')}>
              Employees <span className="role-chip role-employee" style={{ marginLeft: 6 }}>{employeeCount}</span>
            </FilterPill>
          </div>

          {loading ? (
            <div className="empty">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 48 }}>
              <div className="empty-icon">👥</div>
              <div className="empty-title">No users {filter !== 'all' ? `(${filter}s)` : 'yet'}</div>
              <p className="empty-text" style={{ marginBottom: 18 }}>Create accounts so your team can sign in.</p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button className="btn btn-outline-blue" onClick={() => nav('/users/new?role=manager')}>
                  <HiOutlineBriefcase size={16} /> Add Manager
                </button>
                <button className="btn btn-accent" onClick={() => nav('/users/new?role=employee')}>
                  <HiOutlineUserPlus size={16} /> Add Employee
                </button>
              </div>
            </div>
          ) : (
            <div className="card" style={{ padding: 0 }}>
              {filtered.map((u, idx) => {
                const rate = u.employeeRecord?.ratePerHour;
                const cur = u.employeeRecord?.currency || 'GBP';
                return (
                  <div key={u._id} className="user-row" style={{ borderBottom: idx < filtered.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <div className="user-chip-avatar" style={{ width: 44, height: 44, fontSize: 14, opacity: u.isActive ? 1 : 0.45, flexShrink: 0 }}>
                      {initials(u.name)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 3 }}>
                        <span style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</span>
                        <span className={`role-chip role-${u.role === 'user' ? 'employee' : u.role}`}>
                          {u.role === 'user' ? 'employee' : u.role}
                        </span>
                        {!u.isActive && <span className="pill pill-danger">Inactive</span>}
                        {rate > 0 && (
                          <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>
                            · {currencySymbol(cur)}{rate}/hr
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 13, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {u.email}{u.restaurant ? ` · ${u.restaurant.name}` : ''}
                      </div>
                    </div>
                    <div className="user-row-actions">
                      <button className="btn btn-outline-blue btn-sm" onClick={() => nav(`/users/${u._id}/edit`)} title="Edit user">
                        <HiOutlinePencilSquare size={16} />
                      </button>
                      <button className="btn btn-warn btn-sm" onClick={() => askToggle(u)} title={u.isActive ? 'Deactivate' : 'Activate'}>
                        {u.isActive ? <HiOutlinePauseCircle size={16} /> : <HiOutlinePlayCircle size={16} />}
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => askDelete(u)} title="Delete">
                        <HiOutlineTrash size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirm?.type === 'delete'}
        variant="danger"
        title="Delete this user?"
        message={confirm?.user ? `Are you sure you want to permanently delete ${confirm.user.name}? This cannot be undone — their account, shifts and pay records will be removed.` : ''}
        confirmLabel="Yes, delete"
        cancelLabel="Cancel"
        onConfirm={onConfirm}
        onCancel={() => !confirmLoading && setConfirm(null)}
        loading={confirmLoading}
      />

      <ConfirmDialog
        open={confirm?.type === 'toggle'}
        variant="warn"
        title={confirm?.user?.isActive ? 'Deactivate this user?' : 'Activate this user?'}
        message={confirm?.user
          ? confirm.user.isActive
            ? `${confirm.user.name} will no longer be able to sign in. They will not be deleted — you can reactivate them later. Continue?`
            : `${confirm.user.name} will be able to sign in again. Continue?`
          : ''}
        confirmLabel={confirm?.user?.isActive ? 'Yes, deactivate' : 'Yes, activate'}
        cancelLabel="Cancel"
        onConfirm={onConfirm}
        onCancel={() => !confirmLoading && setConfirm(null)}
        loading={confirmLoading}
      />
    </div>
  );
};

export default UserManagement;